import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useAuth, useRoles, PageSkeleton } from '@deloitte-us-consulting/gp-sdk';
import { getRegistry, isLinkedApp, type PluginEntry } from './registry';
import { RemoteMount } from './RemoteMount';
import { LinkedAppRoute } from './LinkedAppRoute';
import { hasPluginAccess } from '../rbac/pluginAccess';
import { useTrackAccess } from '../usage/track';
import NotFound from '../pages/NotFound';
import AccessDenied from '../pages/AccessDenied';

/** Resolve `/:appId/*` → a registered app and either mount it (hosted) or send
 * the user out to it (linked — see LinkedAppRoute). */
export default function RemoteRoute() {
  const { appId } = useParams();
  const { accessToken } = useAuth();
  const rbac = useRoles();
  const [apps, setApps] = useState<PluginEntry[] | null>(null);

  useEffect(() => {
    let alive = true;
    getRegistry(() => accessToken).then((a) => {
      if (alive) setApps(a);
    });
    return () => {
      alive = false;
    };
  }, [accessToken]);

  // Computed before the early returns so the hook below runs on every render. Only an
  // app that resolved and passed the access check counts as an open.
  const found = apps?.find((a) => a.appId === appId);
  useTrackAccess(found && !rbac.isLoading && hasPluginAccess(found, rbac) ? `app:${found.appId}` : null);

  if (apps === null || rbac.isLoading) return <PageSkeleton label="Loading apps…" />;
  const app = apps.find((a) => a.appId === appId);
  if (!app) return <NotFound />;
  // `requiredRole` (set on the registry entry) was previously stored but never
  // enforced anywhere — a registered-but-role-gated app's field was decorative.
  if (!hasPluginAccess(app, rbac)) return <AccessDenied />;
  // A linked app has no bundle — mounting it as a remote would either misreport
  // it as headless or, if it somehow also had a stale remoteUrl, fail on the
  // federation fetch. Route it out instead.
  if (isLinkedApp(app)) return <LinkedAppRoute app={app} />;
  // Keyed on appId: switching between two already-mounted hosted remotes is a
  // param change on the same route element, not a remount, so without this key
  // React treats it as an update rather than swapping to a fresh component —
  // the previous remote's tree (and its own internal router) stays mounted
  // and the new one never mounts.
  return <RemoteMount key={app.appId} app={app} />;
}
